import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { BookOpen, ExternalLink, Clock, Search, AlertCircle } from 'lucide-react';
import { API } from '@/config/api';

const CATEGORY_LABELS = {
  all: 'All topics',
  recovery: 'Recovery & Healing',
  cognitive: 'Cognitive & Nootropic',
  gh_secretagogue: 'GH & Body Composition',
  longevity: 'Longevity & Anti-Aging',
  safety: 'Safety & Best Practices',
  general: 'General',
};

const CATEGORY_COLORS = {
  recovery: 'text-emerald-400 border-emerald-500/40',
  cognitive: 'text-blue-400 border-blue-500/40',
  gh_secretagogue: 'text-amber-400 border-amber-500/40',
  longevity: 'text-purple-400 border-purple-500/40',
  safety: 'text-rose-400 border-rose-500/40',
  general: 'text-slate-300 border-border',
};

export default function Education() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedCategory !== 'all') params.set('category', selectedCategory);
        params.set('limit', '100');
        const res = await fetch(`${API}/education/articles?${params}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setArticles(Array.isArray(data.articles) ? data.articles : []);
          setCategories(['all', ...(data.categories || [])]);
        }
      } catch (e) {
        console.error('Failed to load articles:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [selectedCategory]);

  const filtered = articles.filter((a) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      (a.title || '').toLowerCase().includes(term) ||
      (a.summary || '').toLowerCase().includes(term) ||
      (a.tags || []).some((t) => (t || '').toLowerCase().includes(term))
    );
  });

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="education-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold mb-2 flex items-center gap-2">
          <BookOpen className="h-7 w-7 text-primary" /> Education Hub
        </h1>
        <p className="text-sm md:text-base text-muted-foreground">
          Curated research articles on peptide pharmacology, protocols, and safety.
        </p>
      </div>

      {/* Disclaimer */}
      <Card className="mb-6 p-4 glass-card border-amber-500/30">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm text-muted-foreground">
            All linked materials are for research and educational purposes only. Always cross-reference primary sources before drawing conclusions.
          </p>
        </div>
      </Card>

      {/* Search */}
      <div className="mb-4 relative" data-testid="education-search-wrapper">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="education-search"
          placeholder="Search articles by title, summary, or tag…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Category pills */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2 -mx-1 px-1" data-testid="education-categories">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setSelectedCategory(cat)}
            data-testid={`education-cat-${cat}`}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm border transition-all ${
              selectedCategory === cat
                ? 'border-primary bg-primary/15 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40'
            }`}
          >
            {CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </div>

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="education-loading">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="p-5 glass-card animate-pulse">
              <div className="h-4 bg-secondary/60 rounded w-3/4 mb-2" />
              <div className="h-3 bg-secondary/40 rounded w-full mb-1" />
              <div className="h-3 bg-secondary/40 rounded w-5/6" />
            </Card>
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground glass-card">
          {search.trim() ? 'No articles match your search.' : 'No articles in this category yet.'}
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="education-articles">
          {filtered.map((a) => (
            <Card
              key={a.article_id || a.title}
              className="p-5 glass-card hover:border-primary/40 transition-colors flex flex-col"
              data-testid={`article-${a.article_id || a.title}`}
            >
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wide ${CATEGORY_COLORS[a.category] || CATEGORY_COLORS.general}`}>
                  {CATEGORY_LABELS[a.category] || a.category}
                </span>
                {a.reading_time_min && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" /> {a.reading_time_min} min
                  </span>
                )}
              </div>
              <h3 className="font-semibold text-base mb-2 leading-snug">{a.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-3 mb-3 flex-1">{a.summary}</p>
              {a.tags && a.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {a.tags.slice(0, 4).map((t) => (
                    <Badge key={t} variant="outline" className="text-[10px] font-normal text-muted-foreground">
                      #{t}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-3 mt-auto">
                <span className="truncate mr-2">{a.source || 'Research source'}</span>
                {a.url && (
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline whitespace-nowrap"
                    data-testid={`article-link-${a.article_id || a.title}`}
                  >
                    Read <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
